import { useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { Audience } from '../../lib/library';
import { cx, Input } from '../ui';

interface UserRow {
  id: string;
  name: string;
  email: string;
  status: string;
  roles: string[];
}

/** INT-LIB-04: whole floor, or any mix of markets and named traders. Used for item audiences and required learning. */
export function AudiencePicker({ value, onChange, legend = 'Who can see it' }: { value: Audience; onChange: (a: Audience) => void; legend?: string }) {
  const name = useId();
  const [filter, setFilter] = useState('');
  const markets = useQuery({ queryKey: ['markets'], queryFn: () => api<{ id: string; code: string; name: string }[]>('/markets'), staleTime: 300_000 });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UserRow[]>('/users'), staleTime: 60_000 });
  const traders = useMemo(() => (users.data ?? []).filter((u) => u.roles.includes('TRADER') && u.status === 'ACTIVE'), [users.data]);
  const shown = traders.filter((t) => !filter || t.name.toLowerCase().includes(filter.toLowerCase()) || value.userIds.includes(t.id));
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-2.5 border-0 p-0">
      <legend className="mb-1 text-xs text-muted">{legend}</legend>
      <div className="flex flex-wrap gap-4">
        <label className="flex min-h-10 items-center gap-2">
          <input type="radio" name={name} className="h-[18px] w-[18px]" checked={value.floor} onChange={() => onChange({ floor: true, marketIds: [], userIds: [] })} /> Whole floor
        </label>
        <label className="flex min-h-10 items-center gap-2">
          <input type="radio" name={name} className="h-[18px] w-[18px]" checked={!value.floor} onChange={() => onChange({ ...value, floor: false })} /> Selected markets and traders
        </label>
      </div>
      {!value.floor && (
        <div className="flex flex-col gap-3 rounded-lg border border-line2 bg-raised p-3">
          <div role="group" aria-label="Markets" className="flex flex-wrap gap-1.5">
            {markets.data?.map((m) => {
              const on = value.marketIds.includes(m.id);
              return (
                <button key={m.id} type="button" aria-pressed={on} onClick={() => onChange({ ...value, marketIds: toggle(value.marketIds, m.id) })} className={cx('min-h-9 cursor-pointer rounded-full px-3.5 py-1.5 text-sm', on ? 'border-0 bg-accent font-semibold text-bg' : 'border border-line2 bg-panel text-sub')}>
                  {m.code} traders
                </button>
              );
            })}
          </div>
          <div className="flex flex-col gap-1.5">
            <Input aria-label="Find a trader" placeholder="Find a trader by name" value={filter} onChange={(e) => setFilter(e.target.value)} />
            <div role="group" aria-label="Named traders" className="flex max-h-44 flex-col overflow-y-auto">
              {shown.map((t) => (
                <label key={t.id} className="flex min-h-9 items-center gap-2 px-1">
                  <input type="checkbox" className="h-[18px] w-[18px]" checked={value.userIds.includes(t.id)} onChange={() => onChange({ ...value, userIds: toggle(value.userIds, t.id) })} />
                  <span>{t.name}</span>
                  <span className="truncate text-xs text-muted">{t.email}</span>
                </label>
              ))}
              {!shown.length && <span className="px-1 text-sm text-muted">No traders match.</span>}
            </div>
          </div>
          {!value.marketIds.length && !value.userIds.length && <span className="text-xs text-muted">Pick at least one market or trader.</span>}
        </div>
      )}
    </fieldset>
  );
}
